import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Global keyboard shortcuts. Designed to be unobtrusive: any keystroke
 * while a text input, textarea, contentEditable element, or in-page
 * combobox is focused is ignored. Implements the small set documented in
 * <ShortcutsDialog/>.
 *
 * Bindings:
 *  - "/"     → focus the recipe search input on /my-recipes (handled by
 *              RecipeSearchBar's own listener, so we deliberately skip it
 *              here — duplicating would double-fire).
 *  - "g m"   → navigate to /my-recipes
 *  - "g h"   → navigate to /
 *  - "?"     → open the shortcuts help dialog
 *  - "Esc"   → close the shortcuts help dialog (handled by Radix)
 */
export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useEffect(() => {
    let gPending = false;
    let gTimer: number | null = null;
    const clearG = () => {
      gPending = false;
      if (gTimer !== null) {
        window.clearTimeout(gTimer);
        gTimer = null;
      }
    };

    const ignoreTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.isContentEditable) return true;
      // Radix combobox / listbox roles
      const role = target.getAttribute("role");
      if (role === "combobox" || role === "listbox" || role === "textbox") return true;
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (ignoreTarget(e.target)) return;

      // "?" — open help (Shift+/ on most layouts, but the key value is "?").
      if (e.key === "?") {
        e.preventDefault();
        setShortcutsOpen(true);
        clearG();
        return;
      }

      // Two-key "g <letter>" sequences.
      if (gPending) {
        if (e.key === "m") {
          e.preventDefault();
          navigate("/my-recipes");
        } else if (e.key === "h") {
          e.preventDefault();
          navigate("/");
        }
        clearG();
        return;
      }
      if (e.key === "g") {
        gPending = true;
        gTimer = window.setTimeout(clearG, 1000);
        return;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearG();
    };
  }, [navigate]);

  return { shortcutsOpen, setShortcutsOpen };
};
